import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  FaCamera,
  FaCheckCircle,
  FaExclamationTriangle,
  FaImage,
  FaSpinner,
  FaTimes,
  FaUndo,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { disputeService } from '../services/apiService';
import { formatPrice } from '../utils/currency';
import DisputeClaimDetail from './disputes/DisputeClaimDetail';

const ISSUE_OPTIONS = [
  { value: 'wrong_part', label: 'Wrong part received' },
  { value: 'transit_damage', label: 'Damaged in transit' },
  { value: 'condition_mismatch', label: 'Part not as described (condition mismatch)' },
  { value: 'missing_parts', label: 'Missing parts / incomplete kit' },
  { value: 'counterfeit_oem', label: 'Counterfeit / not OEM as claimed' },
  { value: 'fitment_error', label: 'Part does not fit vehicle (fitment error)' },
];

const TABS = [
  { id: 'raise', label: 'Raise Claim' },
  { id: 'active', label: 'Active Claims' },
  { id: 'resolved', label: 'Resolved Claims' },
];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function StatusChip({ label }) {
  const tone =
    label === 'Admin reviewing'
      ? 'bg-amber-950/50 text-amber-300 border-amber-700'
      : label === 'Decision made'
      ? 'bg-blue-950/50 text-blue-300 border-blue-700'
      : label === 'Refund processed'
      ? 'bg-emerald-950/50 text-emerald-300 border-emerald-700'
      : label === 'Claim auto-rejected' || label === 'No refund'
      ? 'bg-red-950/50 text-red-300 border-red-700'
      : 'bg-slate-800 text-slate-300 border-slate-600';

  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {label}
    </span>
  );
}

/**
 * B-09 — Returns & Disputes
 */
export default function ReturnsDisputes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routeState = location.state || {};
  const initialTab = routeState.tab || searchParams.get('tab') || 'raise';
  const prefillOrderId = routeState.orderId || searchParams.get('orderId') || '';

  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [activeClaims, setActiveClaims] = useState([]);
  const [resolvedClaims, setResolvedClaims] = useState([]);
  const [lastClaimId, setLastClaimId] = useState(routeState.claimId || '');
  const [detailOrderId, setDetailOrderId] = useState(routeState.detailOrderId || '');

  const [forms, setForms] = useState({});

  // Keep URL clean — order/tab context lives in router state, not query string
  useEffect(() => {
    const hasQuery = searchParams.get('orderId') || searchParams.get('tab') || searchParams.get('claimId');
    if (!hasQuery) return;
    navigate('/returns-disputes', {
      replace: true,
      state: {
        tab: searchParams.get('tab') || 'raise',
        orderId: searchParams.get('orderId') || undefined,
        claimId: searchParams.get('claimId') || undefined,
      },
    });
  }, [navigate, searchParams]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eligibleRes, activeRes, resolvedRes] = await Promise.all([
        disputeService.getClaimEligibleOrders(),
        disputeService.listDisputes('active'),
        disputeService.listDisputes('resolved'),
      ]);
      setEligibleOrders(eligibleRes.orders || []);
      setActiveClaims(activeRes.disputes || []);
      setResolvedClaims(resolvedRes.disputes || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const urlTab = routeState.tab;
    if (urlTab && TABS.some((t) => t.id === urlTab)) {
      setTab(urlTab);
    }
    if (routeState.claimId) setLastClaimId(routeState.claimId);
    if (routeState.detailOrderId) setDetailOrderId(routeState.detailOrderId);
  }, [routeState.tab, routeState.claimId, routeState.detailOrderId]);

  useEffect(() => {
    if (prefillOrderId && eligibleOrders.length) {
      setForms((prev) => ({
        ...prev,
        [prefillOrderId]: prev[prefillOrderId] || {
          issueType: '',
          description: '',
          photos: [],
        },
      }));
    }
  }, [prefillOrderId, eligibleOrders]);

  useEffect(() => {
    if (!prefillOrderId || tab !== 'raise' || loading) return undefined;
    const timer = setTimeout(() => {
      document.getElementById(`claim-order-${prefillOrderId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [prefillOrderId, tab, loading, eligibleOrders]);

  const switchTab = (next) => {
    setDetailOrderId('');
    setTab(next);
    navigate('/returns-disputes', {
      replace: true,
      state: { tab: next, orderId: prefillOrderId || undefined, claimId: lastClaimId || undefined },
    });
  };

  const openClaimDetail = (orderId) => {
    setDetailOrderId(orderId);
    navigate('/returns-disputes', {
      replace: true,
      state: {
        tab,
        detailOrderId: orderId,
        claimId: lastClaimId || undefined,
      },
    });
  };

  const closeClaimDetail = () => {
    setDetailOrderId('');
    navigate('/returns-disputes', {
      replace: true,
      state: { tab, claimId: lastClaimId || undefined },
    });
  };

  const updateForm = (orderId, patch) => {
    setForms((prev) => ({
      ...prev,
      [orderId]: { issueType: '', description: '', photos: [], ...prev[orderId], ...patch },
    }));
  };

  const handlePhotoPick = async (orderId, fileList) => {
    const files = Array.from(fileList || []).slice(0, 5);
    const current = forms[orderId]?.photos || [];
    const room = 5 - current.length;
    if (room <= 0) {
      toast.warn('Maximum 5 photos per claim');
      return;
    }
    const picked = files.slice(0, room);
    const encoded = [];
    for (const file of picked) {
      const dataUrl = await readFileAsDataUrl(file);
      encoded.push({ data: dataUrl, fileName: file.name, mimeType: file.type });
    }
    updateForm(orderId, { photos: [...current, ...encoded] });
  };

  const removePhoto = (orderId, index) => {
    const photos = [...(forms[orderId]?.photos || [])];
    photos.splice(index, 1);
    updateForm(orderId, { photos });
  };

  const canSubmit = (orderId) => {
    const f = forms[orderId] || {};
    return (
      f.issueType &&
      (f.description || '').trim().length >= 20 &&
      (f.photos || []).length >= 1
    );
  };

  const [appealForms, setAppealForms] = useState({});

  const handleGracePhotoUpload = async (orderId, fileList) => {
    const files = Array.from(fileList || []).slice(0, 5);
    if (!files.length) return;
    const encoded = [];
    for (const file of files) {
      const dataUrl = await readFileAsDataUrl(file);
      encoded.push({ data: dataUrl, fileName: file.name, mimeType: file.type });
    }
    setSubmitting(true);
    try {
      await disputeService.uploadPhotos(orderId, encoded.map((p) => p.data));
      toast.success('Delivery photo uploaded');
      await loadAll();
    } catch (err) {
      toast.error(err.message || 'Could not upload photo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (order) => {
    const orderId = order.orderId;
    const f = forms[orderId] || {};
    if (!canSubmit(orderId)) return;

    setSubmitting(true);
    try {
      const res = await disputeService.createDispute({
        orderId,
        issueType: f.issueType,
        description: f.description.trim(),
        photos: f.photos.map((p) => p.data || p),
      });
      const payload = res.dispute || res;
      const claim = payload.dispute || payload;
      setLastClaimId(claim.claimId);

      if (claim.isResolved || claim.status === 'auto_rejected') {
        toast.info(claim.outcomeLabel || `Claim ${claim.claimId} recorded.`);
        setTab('resolved');
        navigate('/returns-disputes', { replace: true, state: { tab: 'resolved', claimId: claim.claimId } });
      } else {
        toast.success(`Claim submitted — ID ${claim.claimId}`);
        setTab('active');
        navigate('/returns-disputes', { replace: true, state: { tab: 'active', claimId: claim.claimId } });
      }
      await loadAll();
    } catch (err) {
      toast.error(err.message || 'Could not submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvidenceResponse = async (orderId, fileList) => {
    const files = Array.from(fileList || []).slice(0, 3);
    if (!files.length) return;
    const encoded = [];
    for (const file of files) {
      const dataUrl = await readFileAsDataUrl(file);
      encoded.push(dataUrl);
    }
    setSubmitting(true);
    try {
      await disputeService.submitEvidenceResponse(orderId, {
        party: 'buyer',
        photos: encoded,
        description: 'Additional evidence submitted per admin request',
      });
      toast.success('Evidence submitted');
      await loadAll();
    } catch (err) {
      toast.error(err.message || 'Could not submit evidence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAppeal = async (claim) => {
    const f = appealForms[claim.orderId] || {};
    if ((f.grounds || '').trim().length < 20) {
      toast.warn('Appeal grounds must be at least 20 characters');
      return;
    }
    setSubmitting(true);
    try {
      await disputeService.submitAppeal(claim.orderId, {
        grounds: f.grounds.trim(),
        appellantType: 'buyer',
        newEvidencePhoto: f.photo?.data,
      });
      toast.success('Appeal submitted — senior ops will review within 5 working days');
      setAppealForms((prev) => ({ ...prev, [claim.orderId]: { grounds: '', photo: null } }));
      await loadAll();
    } catch (err) {
      toast.error(err.message || 'Could not submit appeal');
    } finally {
      setSubmitting(false);
    }
  };

  const formatEta = (claim) => {
    if (claim?.statusChip === 'Admin reviewing' || claim?.status === 'open') {
      return 'Resolution target: 72 hours (3 working days)';
    }
    if (!claim?.resolutionEta && !claim?.resolutionDeadline) {
      return 'Resolution target: 72 hours (3 working days)';
    }
    const eta = claim.resolutionDeadline || claim.resolutionEta;
    return `Expected by ${new Date(eta).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  return (
    <div className="min-h-screen bg-[#0b1120] px-4 py-8 text-slate-100">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Returns &amp; Disputes</h1>
            <p className="mt-1 text-sm text-slate-400">
              48-hour claim window after OTP confirmation · payout held until POD or ops review if no OTP in 72h
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/myorder')}
            className="text-sm text-amber-400 hover:text-amber-300"
          >
            My orders
          </button>
        </div>

        <div className="mb-6 flex gap-2 rounded-xl border border-slate-700 bg-slate-900/60 p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => switchTab(t.id)}
              className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id
                  ? 'bg-amber-500 text-slate-900'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-slate-400">
            <FaSpinner className="animate-spin text-2xl" />
          </div>
        ) : detailOrderId ? (
          <DisputeClaimDetail
            orderId={detailOrderId}
            onBack={closeClaimDetail}
            onUpdated={loadAll}
          />
        ) : (
          <>
            {tab === 'raise' && (
              <div className="space-y-5">
                {lastClaimId && (
                  <div className="rounded-xl border border-emerald-700/40 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-200">
                    <FaCheckCircle className="mr-2 inline" />
                    Latest claim ID: <strong className="font-mono">{lastClaimId}</strong>
                  </div>
                )}

                {eligibleOrders.length === 0 ? (
                  <div className="rounded-2xl border border-slate-700 bg-slate-800/40 px-6 py-12 text-center">
                    <FaUndo className="mx-auto mb-3 text-3xl text-slate-500" />
                    <p className="text-slate-300">No orders eligible for return at this time.</p>
                    <p className="mt-2 text-xs text-slate-500">
                      Confirm delivery with your OTP, then raise a claim within 48 hours.
                      hours.
                    </p>
                  </div>
                ) : (
                  eligibleOrders.map((order) => {
                    const f = forms[order.orderId] || {
                      issueType: prefillOrderId === order.orderId ? '' : '',
                      description: '',
                      photos: [],
                    };
                    return (
                      <div
                        key={order.orderId}
                        id={`claim-order-${order.orderId}`}
                        className={`rounded-2xl border bg-slate-800/40 p-5 ${
                          prefillOrderId === order.orderId
                            ? 'border-amber-500/70 ring-1 ring-amber-500/40'
                            : 'border-slate-700/60'
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-mono text-xs text-amber-400">{order.orderId}</p>
                            <h2 className="mt-1 text-lg font-bold text-white">{order.partName}</h2>
                            <p className="text-sm text-slate-400">{formatPrice(order.total)}</p>
                          </div>
                          {order.claimWindowEndsAt && (
                            <p className="text-xs text-amber-300/80">
                              Window closes{' '}
                              {new Date(order.claimWindowEndsAt).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          )}
                        </div>

                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Issue type
                            </label>
                            <select
                              value={f.issueType || ''}
                              onChange={(e) =>
                                updateForm(order.orderId, { issueType: e.target.value })
                              }
                              className="w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-100"
                            >
                              <option value="">Select issue…</option>
                              {ISSUE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Description (min 20 characters)
                            </label>
                            <textarea
                              value={f.description || ''}
                              onChange={(e) =>
                                updateForm(order.orderId, { description: e.target.value })
                              }
                              rows={4}
                              placeholder="Describe the issue in detail…"
                              className="w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-100"
                            />
                            <p className="mt-1 text-xs text-slate-500">
                              {(f.description || '').trim().length}/20 minimum
                            </p>
                          </div>

                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Delivery photo (optional now — 24h grace after submit)
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {(f.photos || []).map((photo, idx) => (
                                <div key={idx} className="relative h-20 w-20 overflow-hidden rounded-lg border border-slate-600">
                                  <img
                                    src={photo.data || photo}
                                    alt={`Evidence ${idx + 1}`}
                                    className="h-full w-full object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(order.orderId, idx)}
                                    className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white"
                                  >
                                    <FaTimes className="text-xs" />
                                  </button>
                                </div>
                              ))}
                              {(f.photos || []).length < 5 && (
                                <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-600 bg-slate-900/50 text-slate-400 hover:border-amber-500 hover:text-amber-400">
                                  <FaCamera className="mb-1" />
                                  <span className="text-[10px]">Add</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                      handlePhotoPick(order.orderId, e.target.files);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                            <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-xs text-blue-400 hover:text-blue-300">
                              <FaImage />
                              Choose from gallery
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={(e) => {
                                  handlePhotoPick(order.orderId, e.target.files);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>

                          <button
                            type="button"
                            disabled={!canSubmit(order.orderId) || submitting}
                            onClick={() => handleSubmit(order)}
                            className="w-full rounded-xl bg-amber-500 py-3 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {submitting ? 'Submitting…' : 'Submit Claim'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {tab === 'active' && (
              <div className="space-y-4">
                {lastClaimId && (
                  <div className="rounded-xl border border-blue-700/40 bg-blue-950/30 px-4 py-3 text-sm text-blue-200">
                    Claim ID: <strong className="font-mono">{lastClaimId}</strong>
                  </div>
                )}
                {activeClaims.length === 0 ? (
                  <p className="rounded-2xl border border-slate-700 bg-slate-800/40 px-6 py-10 text-center text-slate-400">
                    No active claims.
                  </p>
                ) : (
                  activeClaims.map((claim) => (
                    <div
                      key={claim.claimId}
                      className="rounded-2xl border border-slate-700/60 bg-slate-800/40 p-5"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-mono text-xs text-amber-400">{claim.claimId}</p>
                          <p className="mt-1 font-semibold text-white">{claim.partName}</p>
                          <p className="text-xs text-slate-500">{claim.orderId}</p>
                        </div>
                        <StatusChip label={claim.statusChip} />
                      </div>
                      <p className="mt-3 text-sm text-slate-300">{claim.issueLabel}</p>
                      <p className="mt-2 text-xs text-slate-500">{formatEta(claim)}</p>
                      {claim.photoGracePending && (
                        <div className="mt-3 rounded-xl border border-amber-700/50 bg-amber-950/30 px-4 py-3">
                          <p className="text-xs font-semibold text-amber-200">
                            Delivery photo required within 24 hours
                          </p>
                          {claim.photoGraceExpiresAt && (
                            <p className="mt-1 text-xs text-amber-300/80">
                              Grace expires{' '}
                              {new Date(claim.photoGraceExpiresAt).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          )}
                          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-slate-900 hover:bg-amber-400">
                            <FaCamera />
                            Upload delivery photo
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              disabled={submitting}
                              onChange={(e) => {
                                handleGracePhotoUpload(claim.orderId, e.target.files);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      )}
                      {claim.status === 'appealed' && (
                        <div className="mt-3 rounded-xl border border-indigo-700/50 bg-indigo-950/30 px-4 py-3">
                          <p className="text-xs font-semibold text-indigo-200">
                            Your appeal is under senior ops review. You will be notified when a
                            decision is recorded.
                          </p>
                        </div>
                      )}
                      {claim.status === 'evidence_requested' && (
                        <div className="mt-3 rounded-xl border border-blue-700/50 bg-blue-950/30 px-4 py-3">
                          <p className="text-xs font-semibold text-blue-200">
                            Admin requested additional evidence — respond within 48 hours
                          </p>
                          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-500 px-3 py-2 text-xs font-bold text-white hover:bg-blue-400">
                            <FaCamera />
                            Upload requested evidence
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              disabled={submitting}
                              onChange={(e) => {
                                handleEvidenceResponse(claim.orderId, e.target.files);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      )}
                      {claim.suggestedFaultType && (
                        <p className="mt-2 text-xs text-slate-500">
                          System suggestion: {claim.suggestedFaultType.replace(/_/g, ' ')}
                          {claim.arbitrationReason ? ` — ${claim.arbitrationReason}` : ''}
                        </p>
                      )}
                      {claim.adminNotes ? (
                        <p className="mt-3 rounded-lg border border-slate-700 bg-slate-900/50 px-3 py-2 text-xs text-slate-400">
                          <span className="font-semibold text-slate-300">Note: </span>
                          {claim.adminNotes}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => openClaimDetail(claim.orderId)}
                        className="mt-4 w-full rounded-xl border border-slate-600 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800/60"
                      >
                        View claim details
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === 'resolved' && (
              <div className="space-y-4">
                {resolvedClaims.length === 0 ? (
                  <p className="rounded-2xl border border-slate-700 bg-slate-800/40 px-6 py-10 text-center text-slate-400">
                    No resolved claims yet.
                  </p>
                ) : (
                  resolvedClaims.map((claim) => (
                    <div
                      key={claim.claimId}
                      className="rounded-2xl border border-slate-700/60 bg-slate-800/40 p-5"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-mono text-xs text-slate-500">{claim.claimId}</p>
                          <p className="mt-1 font-semibold text-white">{claim.partName}</p>
                          <p className="text-xs text-slate-500">{claim.orderId}</p>
                        </div>
                        <StatusChip label={claim.statusChip} />
                      </div>
                      <p className="mt-3 text-sm text-slate-300">{claim.issueLabel}</p>
                      <div className="mt-4 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Outcome
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-100">
                          {claim.outcome?.type === 'full_refund' && 'Full Refund'}
                          {claim.outcome?.type === 'partial_refund' &&
                            `Partial Refund (${claim.outcome.percent || 50}%)`}
                          {claim.outcome?.type === 'no_refund' && 'No Refund'}
                          {!claim.outcome?.type && 'Decision recorded'}
                        </p>
                        <p className="mt-1 text-sm text-slate-300">
                          {claim.outcomeLabel || 'Decision recorded'}
                        </p>
                        {claim.outcome?.reason && (
                          <p className="mt-1 text-xs text-slate-400">{claim.outcome.reason}</p>
                        )}
                      </div>
                      {claim.appealEligible && (
                        <div className="mt-4 rounded-xl border border-indigo-700/50 bg-indigo-950/30 px-4 py-3">
                          <p className="text-xs font-semibold text-indigo-200">
                            Appeal available until{' '}
                            {claim.appealWindowEndsAt
                              ? new Date(claim.appealWindowEndsAt).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </p>
                          <textarea
                            value={(appealForms[claim.orderId] || {}).grounds || ''}
                            onChange={(e) =>
                              setAppealForms((prev) => ({
                                ...prev,
                                [claim.orderId]: {
                                  ...(prev[claim.orderId] || {}),
                                  grounds: e.target.value,
                                },
                              }))
                            }
                            rows={3}
                            placeholder="Appeal grounds (min 20 chars) — new evidence, procedural error, etc."
                            className="mt-2 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-xs text-slate-100"
                          />
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => handleSubmitAppeal(claim)}
                            className="mt-2 rounded-lg bg-indigo-500 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-400 disabled:opacity-40"
                          >
                            Submit appeal (1 per order)
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => openClaimDetail(claim.orderId)}
                        className="mt-4 w-full rounded-xl border border-slate-600 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800/60"
                      >
                        View claim details
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        <div className="mt-8 flex items-start gap-2 rounded-xl border border-amber-800/40 bg-amber-950/20 px-4 py-3 text-xs text-amber-200/90">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          Confirm delivery to start your 72-hour claim window (auto-confirm after 4 days if not
          confirmed). Upload a delivery photo at submit or within 24 hours — claims without a photo
          after 24h are auto-rejected.
        </div>
      </div>
    </div>
  );
}
